import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { assetUrl, errorMessage } from "../api/client";
import { useClips, useDeleteClip, useRetryClip } from "../api/clips";
import { CLARU_CLIP_DEFINITION } from "../components/ClipComposer";
import ClipStatusBadge from "../components/ClipStatusBadge";
import ConfirmDialog from "../components/ConfirmDialog";
import type { ClipRead, ClipStatus } from "../api/types";
import { formatBytes, formatDate, formatHours } from "../lib/format";

// The Clips library: every clip cut from every recording,
// with per-clip zip download once the worker finishes building it.

const PAGE_SIZE = 50;

const FILTERS: { label: string; key: string; status?: ClipStatus }[] = [
  { label: "All", key: "all" },
  { label: "Queued", key: "queued", status: "queued" },
  { label: "Building", key: "processing", status: "processing" },
  { label: "Ready", key: "ready", status: "ready" },
  { label: "Failed", key: "failed", status: "failed" },
];

function fmtWindow(c: ClipRead): string {
  const start = c.snapped_start_ms ?? c.start_ms;
  const end = c.snapped_end_ms ?? c.end_ms;
  const f = (ms: number) => {
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };
  return `${f(start)}–${f(end)}`;
}

function fmtDuration(c: ClipRead): string {
  const ms = c.duration_ms ?? c.end_ms - c.start_ms;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

export default function ClipsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("f") ?? "all";
  const active = FILTERS.find((f) => f.key === requested) ?? FILTERS[0];
  const [offset, setOffset] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<ClipRead | null>(null);

  const setActive = (key: string) => {
    setOffset(0);
    setSearchParams(key === "all" ? {} : { f: key }, { replace: true });
  };

  const { data, isLoading, error } = useClips({
    status: active.status,
    limit: PAGE_SIZE,
    offset,
  });
  const retry = useRetryClip();
  const del = useDeleteClip();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="clips-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Clips</h1>
          <p className="page-sub">
            {data
              ? `${total} clip${total === 1 ? "" : "s"}${
                  active.key === "all" ? "" : ` (${active.label.toLowerCase()})`
                } · ${formatHours(data.total_duration_ms)} of clip time`
              : "Loading…"}
            <br />
            Activity windows cut losslessly from recordings, packaged for delivery.
            <br />
            <b>What counts as a clip:</b> {CLARU_CLIP_DEFINITION}
          </p>
        </div>
      </div>

      <div className="filter-pills">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={active.key === f.key ? "pill active" : "pill"}
            onClick={() => setActive(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error != null && <p className="error">{errorMessage(error)}</p>}
      {retry.error != null && <p className="error">Retry failed: {errorMessage(retry.error)}</p>}
      {del.error != null && <p className="error">Delete failed: {errorMessage(del.error)}</p>}

      {isLoading ? (
        <div className="skeleton card-skeleton" />
      ) : items.length === 0 ? (
        <div className="empty-state">
          <p className="muted">
            No clips{active.key === "all" ? " yet" : ` in ${active.label.toLowerCase()}`}. Open a
            recording and use <b>Create clips</b> to mark activity windows.
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Original Recording</th>
                <th>Task</th>
                <th>Window</th>
                <th>Duration</th>
                <th>Size</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link className="mono" to={`/r/${c.recording_id}`}>
                      {c.recording_log_id}
                    </Link>
                    <span className="muted small"> · {c.org}</span>
                  </td>
                  <td>{c.task_title ?? c.task_id}</td>
                  <td className="mono">{fmtWindow(c)}</td>
                  <td>{fmtDuration(c)}</td>
                  <td>{c.size_bytes != null ? formatBytes(c.size_bytes) : "—"}</td>
                  <td>
                    <span title={c.error ?? undefined}>
                      <ClipStatusBadge status={c.status} />
                    </span>
                  </td>
                  <td>
                    <span title={c.created_by ?? undefined}>{formatDate(c.created_at)}</span>
                  </td>
                  <td>
                    {c.video_url ? (
                      <Link className="btn small primary" to={`/clips/${c.id}`}>
                        View clip
                      </Link>
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </td>
                  <td className="clip-actions-cell">
                    {c.download_url && (
                      <a className="btn small" href={assetUrl(c.download_url)}>
                        Download
                      </a>
                    )}
                    {c.status === "failed" && (
                      <button
                        className="btn small"
                        disabled={retry.isPending}
                        onClick={() => retry.mutate(c.id)}
                        title={c.error ?? "Re-run the clip build"}
                      >
                        Retry
                      </button>
                    )}
                    <button
                      className="btn small ghost"
                      disabled={c.status === "processing" || del.isPending}
                      onClick={() => setPendingDelete(c)}
                      title={
                        c.status === "processing"
                          ? "The clip is being built; wait for it to finish"
                          : "Delete this clip and its zip"
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="clip-pager">
          <button
            className="btn small"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            ← Newer
          </button>
          <span className="muted small">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <button
            className="btn small"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Older →
          </button>
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete != null}
        title="Delete clip?"
        confirmLabel={del.isPending ? "Deleting…" : "Delete clip"}
        tone="danger"
        busy={del.isPending}
        onConfirm={() => {
          if (pendingDelete) {
            del.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
          }
        }}
        onCancel={() => setPendingDelete(null)}
      >
        <p>
          This removes the clip and its packaged zip
          {pendingDelete ? (
            <>
              {" "}
              (<span className="mono">{pendingDelete.recording_log_id}</span>,{" "}
              {fmtWindow(pendingDelete)})
            </>
          ) : null}
          . The source recording is untouched, so it can be re-clipped.
        </p>
      </ConfirmDialog>
    </div>
  );
}
